import { useContext } from "react";
import { ActionKey, ObjectType } from "common/utils/actionBar";
import { getUserErrors } from "common-client/utils/apollo";
import { useCreateBulkOperationMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { TableRowsSelectionState } from "../Tables/RowSelectionProvider";
import { getTableNameForTracking, pluralizeObjectType } from "./utils";

export function useActionBarRegenerateAIFieldsHandler({
  state,
  objectType,
  onComplete,
  setLoadingAction,
}: {
  state: TableRowsSelectionState;
  objectType: ObjectType;
  onComplete: () => void;
  setLoadingAction: (loadingAction: ActionKey | null) => void;
}) {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { totalSelected } = state;
  const pluralizedObjectType = pluralizeObjectType(objectType, totalSelected);

  const [createBulkOperation] = useCreateBulkOperationMutation({
    onCompleted: data => {
      if (data.createBulkOperation.success) {
        const { message, processedCount } = data.createBulkOperation;
        if (processedCount === 0) {
          addSuccessToast("No records had AI fields to regenerate");
        } else {
          addSuccessToast(
            message ??
              `Successfully regenerated AI fields for ${processedCount} ${pluralizedObjectType}`,
          );
        }
      }
    },
    onError: error => {
      const userError = getUserErrors(error);
      if (userError) {
        addErrorToast(userError);
      } else {
        addErrorToast(
          `An error occurred while regenerating AI fields. Please try again.`,
        );
      }
    },
  });

  const handleRegenerateAIFields = async () => {
    const tableName = getTableNameForTracking(state.queryDescription!);

    track("Bulk action triggered", {
      operation: "regenerateAIFields",
      tableName,
      totalSelected,
    });

    setLoadingAction("regenerateAIFields");

    await createBulkOperation({
      variables: {
        data: {
          objectType,
          operation: "regenerateAIFields",
          selectorParameters: {
            accountId: account!.id,
            mode: state.mode,
            ids: state.ids,
            queryDescription: JSON.stringify(state.queryDescription),
            totalCount: state.totalSelected,
          },
        },
      },
    });

    setLoadingAction(null);
    onComplete();
  };

  return handleRegenerateAIFields;
}
